import { request } from './index';
import { ExamOverviewType } from './exam';
import { PagintionType } from '../utils';
import { LessonType } from './lesson';

export type OptionOverviewType = {
  id: number;
  text: string;
  status: boolean;
  weight: number;
};

export type OptionType = OptionOverviewType & {
  isCorrect: boolean;
};

export type BaseQuestionType = {
  id: number;
  lesson: LessonType;
  question: string;
};

export type QuestionOverviewType = BaseQuestionType & {
  options: OptionOverviewType[];
};

export type QuestionType = BaseQuestionType & {
  options: OptionType[];
  answer: string;
};

export const StudentQuestionBehaviorStates = {
  NORMAL: 'normal',
  WILL_RETURN: 'will_return',
  ABANDONED: 'abandoned'
} as const;

export type StudentQuestionBehaviorStateType =
  typeof StudentQuestionBehaviorStates[keyof typeof StudentQuestionBehaviorStates];

export type StudentQuestionBaseType = {
  id: number;
  option: number | null;
  behaviorState: StudentQuestionBehaviorStateType;
  status: -1 | 0 | 1;
  marked: boolean;
};

export type StudentQuestionOverviewType = StudentQuestionBaseType & {
  question: QuestionOverviewType;
  exam: number;
};

export type StudentQuestionType = StudentQuestionOverviewType & {
  question: QuestionType;
  exam: ExamOverviewType;
};

export type StudentQuestionParamsType = {
  examId?: number;
  lessonId?: number;
  status?: string;
  behaviorState?: string | null;
  lessonIds?: string | undefined;
  marked?: boolean | undefined;
};

export async function fetchStudentQuestions<StudentQuestionType>({
  queryKey: [_, params],
  pageParam = 1
}: {
  queryKey: [string, StudentQuestionParamsType];
  pageParam?: number;
}): Promise<PagintionType<StudentQuestionType>> {
  const urlSearchParams = new URLSearchParams(
    Object.entries({ ...params, page: pageParam })
      .filter(([_, value]) => value !== undefined)
      .map(([key, value]) => [key, String(value)])
  );

  return await request(true, {
    url: `/students/student-questions?${urlSearchParams.toString()}`
  });
}

export async function fetchExamStudentQuestions<StudentQuestionOverviewType>({
  queryKey: [_, { examId }],
  pageParam = 1
}: {
  queryKey: [string, { examId: number }];
  pageParam?: number;
}): Promise<PagintionType<StudentQuestionOverviewType>> {
  return await request(true, {
    url: `/students/student-questions/e-${examId}?page=${pageParam}`
  });
}

export type updateStudentQuestionOptionDataType = {
  studentQuestionId: number;
  optionId: number | null;
  questionNumber: number;
};

export async function updateStudentQuestionOption({
  studentQuestionId,
  optionId
}: updateStudentQuestionOptionDataType): Promise<StudentQuestionOverviewType> {
  return await request(true, {
    method: 'PATCH',
    url: `/students/student-questions/${studentQuestionId}`,
    data: { option: optionId }
  });
}

export type updateStudentQuestionBehaviorStateDataType = {
  studentQuestionId: number;
  behaviorState: StudentQuestionBehaviorStateType;
};

export async function updateStudentQuestionBehaviorState({
  studentQuestionId,
  behaviorState
}: updateStudentQuestionBehaviorStateDataType): Promise<StudentQuestionOverviewType> {
  return await request(true, {
    method: 'PATCH',
    url: `/students/student-questions/${studentQuestionId}`,
    data: { behaviorState }
  });
}

export type updateStudentQuestionMarkedDataType = {
  studentQuestionId: number;
  marked: boolean;
};

export async function updateStudentQuestionMarked({
  studentQuestionId,
  marked
}: updateStudentQuestionMarkedDataType): Promise<StudentQuestionOverviewType> {
  return await request(true, {
    method: 'PATCH',
    url: `/students/student-questions/${studentQuestionId}`,
    data: { marked }
  });
}
